import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Select, Table, DatePicker, notification } from 'antd';
import { PageContainer } from '@ant-design/pro-layout';
import dayjs from 'dayjs';
import { AuthContext } from 'context/AuthContext';
import { formatDateForServer, formatMoney } from '../../../helpers/utils';
import reportService from '../../../services/report.service';
import branchService from '../../../services/branch.service';
import AddAnInvoiceModal from './AddAnInvoiceModal';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TransactionsNotIncludedInvoice = () => {
  const context = useContext(AuthContext);
  const currentMerch = context?.selectedMerch;
  const navigate = useNavigate();
  // local state
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rangeDate, setRangeDate] = useState([dayjs(), dayjs()]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('0');
  const [isAddAnInvoiceModalVisible, setAddAnInvoiceModalVisible] = useState(false);

  const getCurrentMerchBranches = async () => {
    try {
      setLoading(true);
      const number = context?.selectedMerch?.number;
      if (number) {
        const { payload } = await branchService.getBranchesByMerch(number);
        setLoading(false);
        setBranches(payload || []);
      }
    } catch (error) {
      setLoading(false);
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  const getReports = async (currentPage, pageSize) => {
    try {
      setLoading(true);
      if (currentMerch?.number) {
        const { payload } = await reportService.getEodReportsByMerchNotInvoice(
          currentMerch?.number,
          formatDateForServer(rangeDate[0]),
          formatDateForServer(rangeDate[1]),
          selectedBranch,
          0,
          0,
          currentPage,
          pageSize,
        );
        setLoading(false);
        setReports(payload || []);
      }
    } catch (error) {
      setLoading(false);
      setReports([]);
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  useEffect(() => {
    (async () => {
      await getCurrentMerchBranches();
    })();
    // eslint-disable-next-line
  }, [currentMerch]);

  useEffect(() => {
    (async () => {
      await getReports();
    })();
    // eslint-disable-next-line
  }, [rangeDate, selectedBranch, currentMerch]);

  const onDateChange = async (date) => {
    setRangeDate(date);
  };

  const onBranchChange = async (_selectedBranch) => {
    await setSelectedBranch(!selectedBranch ? '0' : _selectedBranch);
  };

  const columns = [
    {
      title: <div className="table-title-calibri-bold-12">Şube ID</div>,
      dataIndex: 'BranchId',
      key: 'BranchId',
      align: 'center',
      width: 100,
      render: (_, row) => {
        return (
          <span className="table-title-calibri-bold-12">{row.BranchId ? row.BranchId : ''}</span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Şube Adı</div>,
      dataIndex: 'BranchName',
      key: 'BranchName',
      align: 'center',
      width: 100,
      render: (_, row) => {
        return (
          <span className="table-title-calibri-bold-12">
            {row.BranchName ? row.BranchName : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Cihaz Sicil Numarası</div>,
      dataIndex: 'terminalSicilNumber',
      key: 'terminalSicilNumber',
      align: 'center',
      width: 200,
      render: (_, row) => {
        return (
          <span className="table-title-calibri-bold-12">
            {row.terminalSicilNumber ? row.terminalSicilNumber : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Gün Sonu Rapor Tarihi</div>,
      dataIndex: 'EodDate',
      key: 'EodDate',
      align: 'center',
      width: 120,
      render: (_, row) => {
        return (
          <span className="table-title-calibri-bold-12">
            {row.EodDate ? dayjs(row.EodDate).format('DD.MM.YYYY') : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Gün Sonu Id</div>,
      dataIndex: 'EodId',
      key: 'EodId',
      align: 'center',
      width: 120,
      render: (_, row) => {
        return <span className="table-title-calibri-bold-12">{row.EodId ? row.EodId : ''}</span>;
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Toplam Gün Sonu Tutarı</div>,
      dataIndex: 'Amount',
      key: 'Amount',
      align: 'center',
      width: 100,
      render: (_, row) => {
        return (
          <div className="text-align-center">
            <span className="table-title-calibri-bold-12">{`${formatMoney(row.TotalEodAmount)} TL`}</span>
          </div>
        );
      },
    },
  ];

  const renderBranchOptions = () => {
    return branches?.map((branch) => (
      <Option key={branch.BranchId} value={branch.BranchId}>
        {branch.BranchTitle}
      </Option>
    ));
  };

  return (
    <>
      <PageContainer
        style={{ paddingBottom: 0 }}
        title="Faturaya Dahil Edilmeyen İşlemler"
        extra={[
          <RangePicker
            allowClear={false}
            defaultValue={rangeDate}
            key="eod"
            onChange={onDateChange}
            format="DD.MM.YYYY"
          />,
          <Select
            key="branch"
            loading={loading}
            defaultValue={null}
            onChange={onBranchChange}
            style={{ width: 200 }}
          >
            <Option value={null} key="ALL">
              Tüm Şubeler
            </Option>
            {renderBranchOptions()}
          </Select>,
          <Button
            key="manual-fatura-kes"
            type="primary"
            onClick={() => {
              setAddAnInvoiceModalVisible(true);
            }}
          >
            Manuel Fatura Kes
          </Button>,
        ]}
      />
      <PageContainer
        style={{ paddingTop: 0 }}
        title=""
        subTitle={`${currentMerch?.number} ${currentMerch?.type}'li şube bazlı faturası kesilmemiş günsonu raporu listelenmektedir.`}
      >
        <Table
          loading={loading}
          dataSource={reports}
          columns={columns}
          pagination
          rowKey={(_, index) => `TransactionsNotIncludedInvoice-${index}`}
          onChange={(page) => {
            getReports(page.current, page.pageSize);
          }}
        />
      </PageContainer>

      <AddAnInvoiceModal
        visible={isAddAnInvoiceModalVisible}
        closeModal={() => {
          setAddAnInvoiceModalVisible(false);
        }}
        branches={branches}
        identificationNumber={currentMerch?.number}
      />
    </>
  );
};

export default TransactionsNotIncludedInvoice;
